import React from "react";

import { FormBox, FormField, FormLabel } from "@impero-com/ui";
import { Checkbox } from "antd";
import type { CheckboxValueType } from "antd/es/checkbox/Group";
import { cx } from "class-variance-authority";

import { ERRORED_CLASSNAME } from "../../../utils/form/erroredElements";
import { GridDirection, GridDisplay } from "../legacyFields/gridDisplay";

import * as theme from "./theme.css";
import type { OptionType } from "./types";

export interface CheckboxGroupInputBaseProps<T> {
    options: OptionType<T>[];
    label?: React.ReactNode;
    disabled?: boolean;
    // Number of columns, defaults to 1
    columns?: number;
    // Grid direction
    direction?: GridDirection;
}

export interface CheckboxGroupInputProps<
    T,
> extends CheckboxGroupInputBaseProps<T> {
    value?: T[];
    error?: string;
    fieldRef?: React.RefCallback<HTMLElement>;
    onChange?: (value: T[]) => void;
    onBlur?: () => void;
}

/**
 * Basic form component based on ant design's Checkbox.Group
 */
export const CheckboxGroupInput = <T extends CheckboxValueType>({
    value,
    onChange,
    onBlur,
    fieldRef,
    error,
    label,
    options,
    disabled = false,
    columns = 1,
    direction,
}: CheckboxGroupInputProps<T>) => {
    const handleChange = (value: CheckboxValueType[]) => {
        if (!onChange) {
            return;
        }
        onChange(value as T[]);
        onBlur?.();
    };
    return (
        <FormBox>
            <FormField
                className={cx(error && ERRORED_CLASSNAME)}
                error={error}
                lite={label === undefined}
            >
                {label && <FormLabel disabled={disabled}>{label}</FormLabel>}
                <Checkbox.Group
                    ref={fieldRef}
                    value={value}
                    disabled={disabled}
                    onChange={handleChange}
                    className={cx(
                        theme.displayBlock,
                        !onChange && theme.nonInteractive
                    )}
                >
                    <GridDisplay
                        columns={columns}
                        items={options}
                        direction={direction}
                        renderItem={(option) => (
                            <Checkbox
                                key={option.value.toString()}
                                value={option.value}
                                disabled={option.disabled}
                            >
                                {option.label}
                            </Checkbox>
                        )}
                    />
                </Checkbox.Group>
            </FormField>
        </FormBox>
    );
};
